import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import React, { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { CancelTemplate } from "@/components/common/Modals/CancelTemplate"
import type { Instruction, InstructionUpdateInput } from "@/graphql/codegen/graphql"
import { FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"
import { useFilesRemove } from "@/hooks/useFilesRemove"
import { useImageUpload } from "@/hooks/useImageUpload"
import { NotificationType } from "@/schemas/notification"
import InstructionForm from "@/screens/ManageProducts/components/Instructions/components/form/InstructionForm"
import UploadInstruction from "@/screens/ManageProducts/components/Instructions/components/form/UploadInstruction"
import {
  EDIT_INSTRUCTION_CANCEL_CONFIRMATION_TEXT,
  EDIT_INSTRUCTION_CANCEL_CONFIRMATION_TITLE,
  EDIT_INSTRUCTION_DIALOG_TITLE,
  UPDATE_INSTRUCTION_TOAST_MESSAGE,
} from "@/screens/ManageProducts/components/Instructions/constants"
import { useInstruction } from "@/screens/ManageProducts/components/Instructions/hooks/useInstruction"
import { useInstructionFileUpload } from "@/screens/ManageProducts/components/Instructions/hooks/useInstructionFileUpload"
import { InstructionFormSchema } from "@/screens/ManageProducts/components/Instructions/schema/InstructionsSchema"
import { InstructionDialogs } from "@/screens/ManageProducts/components/Instructions/types"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UpdateInstructionDialogProps {
  isOpen: InstructionDialogs | null
  instruction: Instruction
  onClose: () => void
  onSuccess: () => void
}

type UpdateInstructionFormType = InstructionUpdateInput & {
  image?: File
}

const EditInstructionDialog: FC<UpdateInstructionDialogProps> = ({ isOpen, instruction, onClose, onSuccess }) => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { updateInstruction } = useInstruction()

  const [fileToRemove, setFileToRemove] = useState<string>()

  const { fileData, refetchFileUploadData } = useInstructionFileUpload({ instructionId: instruction.instructionId })

  const { removeSingleFile } = useFilesRemove(refetchFileUploadData)

  const { uploadFile: uploadImageFile } = useImageUpload({
    referenceTableName: ReferenceTableName.Instruction,
  })

  const methods = useForm<UpdateInstructionFormType>({
    defaultValues: {
      applicableTo: instruction.applicableTo,
      isHazard: instruction.isHazard,
      includeWith: instruction.includeWith,
      name: instruction.name,
      description: instruction.description,
      transportationMode: instruction.transportationMode,
    },
    mode: "all",
    resolver: zodResolver(InstructionFormSchema),
  })

  const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState(false)

  const {
    formState: { isValid, isSubmitting, errors },
    handleSubmit,
  } = methods

  const handleSubmitData = async ({ image, ...fields }: UpdateInstructionFormType) => {
    const { error, data: updatedInstruction } = await updateInstruction({
      input: {
        ...fields,
        instructionId: instruction.instructionId,
      },
    })
    const hasError =
      error?.message ||
      (updatedInstruction?.instruction.update.__typename === "InstructionFailure" &&
        Boolean(updatedInstruction?.instruction.update.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? "Failed to update instruction",
      })
      return
    }

    // Handle file uploads
    if (image) {
      try {
        // Upload image
        const uploadSuccess = await uploadImageFile(
          image,
          FieldGroupEnum.Instruction,
          instruction.instructionId.toString(),
          "instruction_icon"
        )
        if (!uploadSuccess) {
          throw new Error(`Unable to upload image: ${image.name}. Please try again.`)
        }
      } catch (error) {
        notify({
          message: error instanceof Error ? error.message : "Unable to update instruction image. Please try again.",
          type: NotificationType.Error,
        })
      }
    }

    if (fileToRemove) {
      await removeSingleFile(fileToRemove)
    }

    notify({
      type: NotificationType.Success,
      silent: false,
      content: UPDATE_INSTRUCTION_TOAST_MESSAGE,
    })
    onSuccess()
  }

  const handleConfirmCancel = () => {
    onClose()
  }

  const handleCancelDialog = () => {
    setCancelConfirmationOpen(true)
  }

  const onMarkFileAsNeedRemoval = () => {
    const fileIdToRemove = fileData[0]?.file?.fileId
    if (fileIdToRemove && setFileToRemove) {
      setFileToRemove(fileIdToRemove)
    }
  }

  return (
    <Dialog open={isOpen === InstructionDialogs.EDIT_INSTRUCTION} onClose={handleCancelDialog} fullWidth>
      {cancelConfirmationOpen ? (
        <CancelTemplate
          onClose={setCancelConfirmationOpen}
          title={EDIT_INSTRUCTION_CANCEL_CONFIRMATION_TITLE}
          message={EDIT_INSTRUCTION_CANCEL_CONFIRMATION_TEXT}
          onConfirm={handleConfirmCancel}
        />
      ) : (
        <>
          <DialogTitle className='pl-10 font-normal text-primary'>{EDIT_INSTRUCTION_DIALOG_TITLE}</DialogTitle>
          <IconButton aria-label='close' onClick={handleCancelDialog} className='absolute right-4 top-4 text-primary'>
            <CloseOutlined />
          </IconButton>
          <Divider />
          <DialogContent classes={{ root: "p-10" }}>
            <FormProvider {...methods}>
              <InstructionForm />
              <UploadInstruction
                title='Upload Instruction Icon'
                fileData={fileData[0]?.file ? (fileData[0].file as File) : undefined}
                onChange={onMarkFileAsNeedRemoval}
                name='image'
              />
            </FormProvider>
          </DialogContent>
          <DialogActions className='justify-between px-10 pb-10'>
            <Button appearance='outlined' onClick={handleCancelDialog}>
              Cancel
            </Button>
            <Button
              disabled={!isValid || isSubmitting || Boolean(Object.keys(errors).length)}
              onClick={handleSubmit(handleSubmitData)}
            >
              Update
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default EditInstructionDialog
